import { useEffect, useState } from 'react';
import { apiFetch } from '~/lib/api';
import { Tone, WaitingInline } from '~/components/ui';
import { messages, useT } from '~/i18n';
import { fetchOnboardingStatus } from './api/onboardingClient';
import { invalidateOnboardingGate } from '~/components/shell/useOnboardingGate';
import { PublicAckCheckbox } from './components/ConsentStep';

/**
 * El consentimiento de la bolsa de talento, ahora una cara propia del perfil.
 *
 * Estaba como la última tarjeta de Preferencias, debajo de siete campos. Es la
 * decisión más pesada que toma el candidato en esta app —quién puede verlo— y
 * merece su propia pantalla, no el pie de un formulario.
 *
 * Su propio endpoint, además del suyo propio: guardar preferencias JAMÁS puede
 * mover este interruptor, y mover el interruptor no puede depender de que el
 * formulario esté válido. Consentir no es una preferencia.
 */
export default function JobApplyTalentPoolPage() {
  const t = useT('onboarding');
  /**
   * ADENTRO es el default del producto desde el cambio del backend: las altas
   * nuevas nacen visibles y el que quiera salirse se sale. Por eso el estado
   * inicial es `true` y la lectura de abajo apaga sólo con un `false` explícito
   * — un campo ausente es "todavía no se escribió el default", no una negativa.
   *
   * (Una cuenta vieja sin el backfill corrido llega con `false` de verdad y se
   * ve apagada. Es la dirección segura para equivocarse: nadie aparece en una
   * búsqueda sin que el dato esté escrito.)
   */
  const [optIn, setOptIn] = useState(true);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  /**
   * El perfil público. Default PRIVADO: sin la palabra del servidor
   * (`public_profile` de onboarding-status) nadie aparece en Google.
   */
  const [isPublic, setIsPublic] = useState(false);
  /** Pasar a público pide la casilla «Entiendo…» antes de guardar. */
  const [confirmingPublic, setConfirmingPublic] = useState(false);
  const [ack, setAck] = useState(false);
  const [ackProblem, setAckProblem] = useState(false);
  /**
   * `free_tier` de onboarding-status. Sin él (interruptor apagado o sin la
   * palabra del servidor) la pantalla es la de antes: sin perfil público y con
   * su título viejo.
   */
  const [freeTier, setFreeTier] = useState(false);
  /** La hoja pública, de onboarding-status (`public_profile_url`). */
  const [publicUrl, setPublicUrl] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const refreshPublicUrl = () =>
    fetchOnboardingStatus()
      .then((st) => setPublicUrl(st.public_profile_url ?? null))
      .catch(() => {});

  useEffect(() => {
    let cancelled = false;
    apiFetch('/job-apply/api/dashboard')
      .then((res) => (res.ok ? res.json() : null))
      .then((data) => {
        if (cancelled || !data?.membership) return;
        setOptIn(data.membership.talent_pool_opt_in !== false);
      })
      .catch(() => {
        if (!cancelled) setErr(messages().onboarding.talentPool.loadFailed);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    fetchOnboardingStatus()
      .then((st) => {
        if (cancelled) return;
        setIsPublic(st.public_profile === 'public');
        setFreeTier(st.free_tier === true);
        setPublicUrl(st.public_profile_url ?? null);
      })
      .catch(() => {
        // Sin la lectura queda privado: es la dirección segura.
      });
    return () => { cancelled = true; };
  }, []);

  const toggle = async (next: boolean) => {
    setSaving(true);
    setErr(null);
    setNotice(null);
    try {
      const res = await apiFetch('/job-apply/api/talent-pool', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        // Un booleano, nunca una cadena: el endpoint se niega a interpretar
        // un consentimiento.
        body: JSON.stringify({ opt_in: next }),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) {
        setErr(body?.error || t.talentPool.saveFailed);
        return;
      }
      setOptIn(body.talent_pool_opt_in === true);
      setNotice(
        body.talent_pool_opt_in === true
          ? t.talentPool.joined
          : t.talentPool.left,
      );
    } catch {
      setErr(t.talentPool.connectionError);
    } finally {
      setSaving(false);
    }
  };

  const saveProfile = async (next: 'public' | 'private') => {
    if (next === 'public' && !ack) {
      setAckProblem(true);
      return;
    }
    setSaving(true);
    setErr(null);
    setNotice(null);
    try {
      const res = await apiFetch('/job-apply/api/talent-pool', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(next === 'public' ? { public_profile: 'public', public_ack: true } : { public_profile: 'private' }),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) {
        if (body?.code === 'public_ack_required') {
          setAck(false);
          setAckProblem(true);
        } else {
          setErr(t.talentPool.saveFailed);
        }
        return;
      }
      setIsPublic(next === 'public');
      // El botón «Ver mi perfil público» del encabezado de Perfil sale del gate:
      // que la próxima pestaña lo pida de nuevo y no muestre el estado de antes.
      invalidateOnboardingGate();
      if (next === 'public') void refreshPublicUrl();
      else setPublicUrl(null);
      setConfirmingPublic(false);
      setAck(false);
      setAckProblem(false);
      setNotice(next === 'public' ? t.talentPool.profileMadePublic : t.talentPool.profileMadePrivate);
    } catch {
      setErr(t.talentPool.connectionError);
    } finally {
      setSaving(false);
    }
  };

  const toggleProfile = (next: boolean) => {
    setErr(null);
    setNotice(null);
    if (next) {
      // Todavía no se guarda nada: primero la casilla.
      setConfirmingPublic(true);
      return;
    }
    setConfirmingPublic(false);
    setAck(false);
    setAckProblem(false);
    // Nunca fue público: nada que guardar.
    if (!isPublic) return;
    void saveProfile('private');
  };

  if (loading) return <WaitingInline label={t.talentPool.loading} />;

  return (
    <div className="space-y-4">
      {err && <div className="alert alert-error text-sm">{err}</div>}
      {notice && <div className="alert alert-success text-sm">{notice}</div>}

      <div className="card">
        <div className="card-body space-y-3">
          <div className="flex items-start justify-between gap-4">
            <div>
              <div className="flex items-center gap-2">
                <h2 className="card-title text-lg">{freeTier ? t.talentPool.title : t.talentPool.titleLegacy}</h2>
                {/* El estado se DICE, no se deduce del switch: un toggle a la
                    derecha de un párrafo largo se lee mal. */}
                <Tone tone={optIn ? 'good' : 'muted'}>{optIn ? t.talentPool.on : t.talentPool.off}</Tone>
              </div>
              <p className="text-sm hush">{t.talentPool.lede}</p>
            </div>
            <input
              type="checkbox"
              className="toggle toggle-primary mt-1"
              aria-label={t.talentPool.toggleLabel}
              checked={optIn}
              disabled={saving}
              onChange={(e) => toggle(e.target.checked)}
            />
          </div>

          {/* El perfil público solo existe con free_tier. */}
          {freeTier && (
            <>
              <div className="flex items-start justify-between gap-4" data-testid="public-profile-row">
                <div>
                  <h3 className="font-semibold">{t.talentPool.profileTitle}</h3>
                  <p className="text-sm hush">{t.talentPool.profileLede}</p>
                </div>
                <input
                  type="checkbox"
                  className="toggle toggle-primary mt-1"
                  aria-label={t.talentPool.profileToggleLabel}
                  checked={isPublic || confirmingPublic}
                  disabled={saving}
                  onChange={(e) => toggleProfile(e.target.checked)}
                />
              </div>
              {isPublic && publicUrl && (
                <div className="flex flex-wrap items-center gap-2" data-testid="public-profile-link">
                  <a className="btn btn-outline btn-sm" href={publicUrl} target="_blank" rel="noopener">
                    {t.talentPool.profileView}
                  </a>
                  <button
                    type="button"
                    className="btn btn-ghost btn-sm"
                    onClick={() => {
                      navigator.clipboard?.writeText(publicUrl).then(
                        () => setCopied(true),
                        () => {},
                      );
                    }}
                  >
                    {copied ? t.talentPool.profileCopied : t.talentPool.profileCopy}
                  </button>
                </div>
              )}
              {confirmingPublic && !isPublic && (
                <div className="space-y-2">
                  <PublicAckCheckbox
                    id="talent-pool-public-ack"
                    checked={ack}
                    onChange={(v) => {
                      setAck(v);
                      if (v) setAckProblem(false);
                    }}
                    error={ackProblem}
                    disabled={saving}
                  />
                  <div className="flex gap-2">
                    <button type="button" className="btn btn-primary btn-sm" disabled={saving} onClick={() => saveProfile('public')}>
                      {t.talentPool.profileSave}
                    </button>
                    <button
                      type="button"
                      className="btn btn-ghost btn-sm"
                      disabled={saving}
                      onClick={() => {
                        setConfirmingPublic(false);
                        setAck(false);
                        setAckProblem(false);
                      }}
                    >
                      {t.talentPool.profileCancel}
                    </button>
                  </div>
                </div>
              )}
            </>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
            <div
              className="p-3 rounded-2xl"
              style={{ background: 'var(--surface-hi)', border: '1px solid var(--hairline)' }}
            >
              <p className="font-medium">{t.talentPool.seenTitle}</p>
              <p className="hush">{t.talentPool.seenBody}</p>
            </div>
            <div
              className="p-3 rounded-2xl"
              style={{ background: 'var(--surface-hi)', border: '1px solid var(--hairline)' }}
            >
              <p className="font-medium">{t.talentPool.neverSeenTitle}</p>
              <p className="hush">{t.talentPool.neverSeenBody}</p>
            </div>
          </div>

          {/* El pie cambia con el estado, y el de "apagada" no regaña: dice qué
              se está perdiendo y deja la puerta abierta. Aparecer es el default
              del producto, así que estar afuera es la excepción — pero es SU
              excepción, y sale con el mismo interruptor con el que entró. */}
          <p className="text-xs hush">
            {optIn ? t.talentPool.footerOn : t.talentPool.footerOff}
          </p>
        </div>
      </div>
    </div>
  );
}
